/**
 * Shown above the native event wizard when it was seeded from a poster: the poster
 * image, the assistant's one-line summary, any warnings, and two "from poster / from
 * your recent events" cards. Purely presentational — driven by the `sources` map.
 */
import { Alert, Card, Image, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";

const LABELS = {
  "/title": "Title",
  "/description": "Description",
  "/event_access": "Access",
  "/start_date_time": "Date & time",
  "/organiser_name": "Organiser",
  "/contact_email": "Email",
  "/contact_phone": "Phone",
  "/tickets/0/price": "Ticket price",
  "/tickets/0/quantity": "Capacity",
  "/activities": "Activities",
  "/amenities": "Amenities",
  "/media": "Cover photo",
  "/tags": "Tags",
};
const SKIP = new Set([
  "/organiser_logo_url",
  "/community_id",
  "/location_mode",
  "/hsn_code",
  "/is_featured",
  "/show_in_listings",
  "/end_date_time",
]);

function getByPath(obj, ptr) {
  return ptr
    .replace(/^\//, "")
    .split("/")
    .reduce((a, k) => (a === null ? undefined : a[k]), obj);
}

function fmt(path, v) {
  if (v === null || v === "") return null;
  if (Array.isArray(v)) {
    if (!v.length) return null;
    if (path === "/media") return `${v.length} image${v.length > 1 ? "s" : ""}`;
    const names = v.map((x) => x?.name ?? x).filter(Boolean);
    return names.length ? names.join(", ") : `${v.length} selected`;
  }
  if (path === "/start_date_time") {
    const d = dayjs(v);
    return d.isValid() ? d.format("ddd, D MMM YYYY · h:mm A") : null;
  }
  if (path === "/event_access")
    return v === "PUBLIC" ? "Public" : v === "MEMBERS_ONLY" ? "Members-Only" : String(v);
  if (path === "/location_id") return "your usual venue";
  if (path === "/description")
    return String(v).length > 60 ? String(v).slice(0, 60) + "…" : String(v);
  if (typeof v === "object") return null;
  return String(v);
}

// Map any (possibly granular) source path to a clean label + the value path to read.
function resolve(path) {
  if (/^\/activities(\/|$)/.test(path)) return { label: "Activities", valuePath: "/activities" };
  if (/^\/amenities(\/|$)/.test(path)) return { label: "Amenities", valuePath: "/amenities" };
  if (/^\/location(\/|$)/.test(path) || path === "/location_id")
    return { label: "Venue", valuePath: path === "/location_id" ? "/location_id" : "/location/name" };
  if (/^\/media(\/|$)/.test(path)) return { label: "Cover photo", valuePath: "/media" };
  if (/^\/tickets\/\d+\/price$/.test(path)) return { label: "Ticket price", valuePath: path };
  if (/^\/tickets\/\d+\/quantity$/.test(path)) return { label: "Capacity", valuePath: path };
  if (LABELS[path]) return { label: LABELS[path], valuePath: path };
  const seg = path.replace(/^\//, "").split("/").pop() || path;
  return { label: seg.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()), valuePath: path };
}

function rowsForSource(sources, model, src) {
  const seen = new Set();
  const rows = [];
  for (const [path, s] of Object.entries(sources || {})) {
    if (s !== src || SKIP.has(path)) continue;
    const { label, valuePath } = resolve(path);
    if (seen.has(label)) continue;
    const value = fmt(valuePath, getByPath(model, valuePath));
    if (value === null) continue;
    seen.add(label);
    rows.push({ label, value });
  }
  return rows;
}

const KV = ({ rows }) =>
  rows.length ? (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            borderBottom: "1px dashed var(--border,#333)",
            paddingBottom: 4,
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {r.label}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 13, textAlign: "right" }}>
            {r.value}
          </Typography.Text>
        </div>
      ))}
    </Space>
  ) : (
    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
      nothing
    </Typography.Text>
  );

function PosterProvenance({ coverUrl, sources, model, summary, warnings, hasHistory, missing }) {
  if (!sources || !model) return null;
  const posterRows = rowsForSource(sources, model, "image");
  const historyRows = rowsForSource(sources, model, "history");

  return (
    <div style={{ margin: "16px 0 8px" }}>
      <Space size={16} align="start" style={{ display: "flex", width: "100%", flexWrap: "wrap" }}>
        {coverUrl ? (
          <Image
            src={coverUrl}
            alt="Event poster"
            style={{
              maxHeight: 180,
              borderRadius: 12,
              objectFit: "cover",
              border: "1px solid var(--border,#333)",
            }}
          />
        ) : null}
        <div style={{ flex: 1, minWidth: 280 }}>
          <Typography.Paragraph style={{ marginBottom: 12 }}>
            <Tag color="purple" style={{ marginRight: 8 }}>
              Assistant
            </Tag>
            {summary
              ? summary
              : `I read ${posterRows.length} field${posterRows.length === 1 ? "" : "s"} from your poster${hasHistory ? " and pulled details from your recent events" : ""}.`}{" "}
            Everything is pre-filled below — review and complete the required fields.
          </Typography.Paragraph>

          {warnings?.length ? (
            <Space direction="vertical" size={6} style={{ width: "100%", marginBottom: 12 }}>
              {warnings.map((w, i) => (
                <Alert
                  key={i}
                  type={w.level === "warning" ? "warning" : "info"}
                  message={w.message}
                  showIcon
                />
              ))}
            </Space>
          ) : null}

          {missing?.length > 1 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Still needs your input"
              description={missing.join(" · ")}
            />
          ) : null}

          <Space size={14} align="start" style={{ display: "flex", width: "100%", flexWrap: "wrap" }}>
            <Card size="small" title="From the poster" style={{ flex: 1, minWidth: 230 }}>
              <KV rows={posterRows} />
            </Card>
            <Card size="small" title="From your recent events" style={{ flex: 1, minWidth: 230 }}>
              <KV rows={historyRows} />
            </Card>
          </Space>
        </div>
      </Space>
    </div>
  );
}

export default PosterProvenance;
